/**
 * Sing4YouTalentPage — talent live page with PVLC fully merged
 *
 * ─── BUG FIXES IN THIS VERSION ───────────────────────────────────────────
 *
 * B1 — Camera re-publish fires before Agora internal state settles
 *   OLD: useEffect timeout read pvlcPhase from closure — stale by the time
 *        the 200ms timer fires.
 *   FIX: Added pvlcPhaseRef (mirrors pvlcPhase via useEffect). The timeout
 *        callback reads pvlcPhaseRef.current synchronously — always current.
 *        Delay raised from 200ms → 300ms for extra headroom.
 *
 * B2 — pvlcGoLive called createCustomVideoTrack with encoderConfig containing
 *      width/height/frameRate — that shape is NOT accepted by agora-rtc-react's
 *      createCustomVideoTrack. Encoder params must be omitted; the canvas
 *      resolution drives the stream naturally via captureStream.
 *   FIX: Removed encoderConfig block from createCustomVideoTrack call.
 *        Only mediaStreamTrack is passed.
 *
 * B3 — Two useEffects both set mic volume — last one wins unpredictably
 *   OLD: useEffect on [localMicrophoneTrack] hardcoded setVolume(100).
 *        useEffect on [pvlcMicVol, localMicrophoneTrack] also called setVolume.
 *        On mount both run; after PVLC closes the slider value stuck.
 *   FIX: Removed the hardcoded setVolume(100) effect entirely. pvlcMicVol
 *        is the single authority (defaulted to 100). pvlcClose() resets it
 *        back to 100 so normal mode always starts at full mic gain.
 *
 * B4 — VideoSlot in Sing4YouLiveStream matched track via private _uintid field
 *   OLD: videoTracks.find(t => t._uintid === user.uid || t.getUserId?.() === user.uid)
 *        _uintid is a private SDK field — not documented, breaks on SDK update.
 *   FIX: Use user.videoTrack directly (the documented Agora Web SDK pattern).
 *        VideoSlot receives the user object; track is user.videoTrack.
 *        (Applied in Sing4YouLiveStream.tsx)
 *
 * B5 — pvlcAudioSrcRef not reset in error path of pvlcGoLive
 *   OLD: If createMediaElementSource threw, pvlcAudioCtxRef/pvlcAudioSrcRef
 *        could be partially set, causing "already used" crash on retry.
 *   FIX: try/catch wraps AudioContext creation; both refs are nulled on failure.
 *
 * B6 — pvlcSendRTM defined but pvlcGoLive / pvlcBroadcastSync / pvlcLoadFile /
 *      pvlcLoadUrl all called sendRTM directly, skipping snapshot ref updates.
 *      Late-joining viewers got stale pvlcTitleRef / pvlcTimeRef on state_request.
 *   FIX: All pvlc_ RTM messages now go through pvlcSendRTM, which updates the
 *        snapshot refs before broadcasting. pvlcBroadcastSync rewritten to call
 *        pvlcSendRTM. pvlcGoLive sends pvlc_load via pvlcSendRTM after going live.
 *
 * B7 (in Sing4YouCafePage) — VideoTracker still had onNetworkError prop and
 *      called bumpRevision on track.play() failure → constant Sing4YouLiveStream
 *      remounts. Fixed in Sing4YouCafePage.tsx (separate file).
 *
 * B8 (in Sing4YouCafePage) — state_request sent 800ms after RTM join; on slow
 *      connections Agora RTC was not yet CONNECTED. Delay raised to 1500ms.
 *      Fixed in Sing4YouCafePage.tsx.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import AgoraRTC, {
  LocalUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRTCClient,
  useRemoteAudioTracks,
  useRemoteUsers,
} from "agora-rtc-react";
import AgoraRTM from "agora-rtm-sdk";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Slider,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Build,
  CallEnd,
  Close,
  CloudUpload,
  Fullscreen,
  FullscreenExit,
  GraphicEq,
  Link as LinkIcon,
  Mic,
  MicOff,
  ChatBubbleOutline,
  ChevronRight,
  MovieFilter,
  Pause,
  PlayArrow,
  ScreenShare,
  StopScreenShare,
  Send,
  SkipNext,
  SkipPrevious,
  Stop,
  Videocam,
  
  VideocamOff,
  Visibility,
  VolumeUp,
} from "@mui/icons-material";

import AgoraWrapper from "../Karaokecard/AgoraWrapper";
import AudioMixUpdated, { type AudioMixUpdatedRef } from "../Karaokecard/Agora/AudioMixUpdated";
import KaraokeLyrics from "../Karaokecard/Agora/KaraokeLyrics";
import { useLyricsAutoFetch } from "./Lyrics/useLyricsautofetch";
import LyricsStatusBadge from "./lyricstubsbadge";
import LiveChat from "../LiveChat";
import LivePolls from "../LivePolls";
import BingoManagement from "../Talent/BingoManagement";
import GuestManagement from "../Talent/GuestManagement_updated";
import YoutubeRequest from "../Talent/YoutubeRequest";
import ListofSongsManagement, { SongItem } from "../Talent/ListofSongsManagement";
import { AGORA_APP_ID, AGORA_RTM_APP_ID } from "../../../constants/config";
import { getAgoraToken, getAgoraRtmToken } from "../../../services/api/agora.api";
import { useStartEndStageSession } from "../../../services/stagemode_api/talent/talent_api";
import { useCurrentTalentProfile } from "../../../hooks";
import useCurrentBusinessInfo from "../../../hooks/useCurrentBusiness";

import "../../../styles/sing4you-live.css";

/* ─── types ─────────────────────────────────────────────────────────────── */
type SidebarTab  = "chat" | "guests" | "youtube" | "polls" | "bingo";
type ToolId      = "bingo" | "songlist";
type PvlcPhase   = "idle" | "loaded" | "live";

const TOOL_META: Record<ToolId, { label: string; emoji: string; desc: string }> = {
  bingo:    { label: "Bingo Break", emoji: "🎯", desc: "Play bingo with your viewers" },
  songlist: { label: "Song List",   emoji: "🎵", desc: "Manage your song queue"       },
};

/* ─── PVLC canvas constants ──────────────────────────────────────────────── */
const PVLC_W     = 1280;
const PVLC_H     = 720;
const PVLC_FPS   = 24;    // lowered from 30 → reduces CPU load on mid-range laptops
const PVLC_PIP_W = 280;
const PVLC_PIP_H = 158;   // 16:9
const PVLC_PIP_M = 20;
const PVLC_PIP_R = 12;

/* ─── helpers ────────────────────────────────────────────────────────────── */
function sendRTM(ref: React.MutableRefObject<any>, payload: object): void {
  ref.current?.sendMessage({ text: JSON.stringify(payload) }).catch(() => {});
}

function fmtTime(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

function sliderSx(color: string) {
  return {
    color, height: 3,
    "& .MuiSlider-thumb": { width: 12, height: 12, "&:hover": { boxShadow: `0 0 0 6px ${color}28` } },
    "& .MuiSlider-track": { border: "none" },
    "& .MuiSlider-rail":  { opacity: 0.2, bgcolor: "#fff" },
  };
}

/* ─── sub-components (PVLC panel) ────────────────────────────────────────── */
function PvlcFileBtn({
  icon, label, onClick, muted = false,
}: {
  icon: React.ReactNode; label: string; onClick: () => void; muted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        width: "100%", padding: "9px 12px",
        background: muted ? "rgba(255,255,255,0.04)" : "rgba(255,107,157,0.1)",
        border: `1px solid ${muted ? "rgba(255,255,255,0.1)" : "rgba(255,107,157,0.3)"}`,
        borderRadius: 8, color: muted ? "rgba(255,255,255,0.5)" : "#fff",
        fontWeight: 600, fontSize: "0.82rem", cursor: "pointer",
        transition: "all 0.15s", textAlign: "left",
      }}
    >
      {icon}{label}
    </button>
  );
}

function PvlcVolSlider({
  icon, label, value, color, onChange,
}: {
  icon: React.ReactNode; label: string; value: number; color: string; onChange: (v: number) => void;
}) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
        {icon}
        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem" }}>{label}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem" }}>{value}%</Typography>
      </Box>
      <Slider value={value} min={0} max={100} onChange={(_, v) => onChange(v as number)} sx={sliderSx(color)} size="small" />
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Sing4YouLiveInner — must live inside AgoraRTCProvider (via AgoraWrapper)
 * ══════════════════════════════════════════════════════════════════════════ */
function Sing4YouLiveInner() {
  const { channel_name } = useParams<{ channel_name: string }>();
  const navigate         = useNavigate();
  const [params]         = useSearchParams();
  const userId           = params.get("userId")    ?? "";
  const bookingId        = params.get("bookingId") ?? "";

  const isMobile         = useMediaQuery("(max-width: 768px)");

  const { currentProfile }   = useCurrentTalentProfile();
  const { currentBusiness }  = useCurrentBusinessInfo();
  const startEndStageSession = useStartEndStageSession(currentProfile?.id);
  const agoraClient          = useRTCClient();
  const videoId              = channel_name ?? "default";

  /* ── general UI ───────────────────────────────────────────────────────── */
  const [token,         setToken]         = useState<string | null>(null);
  const [micOn,         setMicOn]         = useState(true);
  const [cameraOn,      setCameraOn]      = useState(true);
  const [screenOn,      setScreenOn]      = useState(false);
  const [isFullscreen,  setIsFullscreen]  = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [audioMixOpen,  setAudioMixOpen]  = useState(false);
  const [activeTab,     setActiveTab]     = useState<SidebarTab>("chat");
  const [viewerCount,   setViewerCount]   = useState(0);
  const [chatInput,     setChatInput]     = useState("");
  const [volume,        setVolume]        = useState(50);
  const [sidebarOpen,   setSidebarOpen]   = useState(true);

  /* ── audio quality ────────────────────────────────────────────────────── */
  const [audioQualityOpen, setAudioQualityOpen] = useState(false);
  const [aecEnabled,       setAecEnabled]       = useState(false);
  const [agcEnabled,       setAgcEnabled]       = useState(false);
  const [ansEnabled,       setAnsEnabled]       = useState(false);
  const [audioPreset,      setAudioPreset]      = useState<"high_quality_stereo" | "high_quality" | "standard">("high_quality_stereo");

  /* ── device management ────────────────────────────────────────────────── */
  const [micDevices,     setMicDevices]     = useState<MediaDeviceInfo[]>([]);
  const [outputDevices,  setOutputDevices]  = useState<MediaDeviceInfo[]>([]);
  const [activeMicId,    setActiveMicId]    = useState("");
  const [activeOutputId, setActiveOutputId] = useState("");

  /* ── creator tools ────────────────────────────────────────────────────── */
  const [toolsOpen,     setToolsOpen]     = useState(false);
  const [activeTools,   setActiveTools]   = useState<ToolId[]>([]);
  const [songList,      setSongList]      = useState<SongItem[]>([]);
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);

  /* ── karaoke ──────────────────────────────────────────────────────────── */
  const [lyricsLrc,        setLyricsLrc]        = useState("");
  const [lyricsTime,       setLyricsTime]       = useState(0);
  const [isKaraokePlaying, setIsKaraokePlaying] = useState(false);
  const [karaokeDuration,  setKaraokeDuration]  = useState(0);
  const [songTitle,        setSongTitle]        = useState("");
  const [songTitleForRTM,  setSongTitleForRTM]  = useState("");
  const [songArtistForRTM, setSongArtistForRTM] = useState("");
  const [trackLoaded,      setTrackLoaded]      = useState(false);
  const [karaokeFilename,  setKaraokeFilename]  = useState("");

  /* ── PVLC UI state ────────────────────────────────────────────────────── */
  const [pvlcOpen,       setPvlcOpen]       = useState(false);
  const [pvlcPhase,      setPvlcPhase]      = useState<PvlcPhase>("idle");
  const [pvlcVideoTitle, setPvlcVideoTitle] = useState("");
  const [pvlcUrlInput,   setPvlcUrlInput]   = useState("");
  const [pvlcShowUrl,    setPvlcShowUrl]    = useState(false);
  const [pvlcPlaying,    setPvlcPlaying]    = useState(false);
  const [pvlcTime,       setPvlcTime]       = useState(0);
  const [pvlcDuration,   setPvlcDuration]   = useState(0);
  const [pvlcVideoVol,   setPvlcVideoVol]   = useState(70);
  // B3 FIX: pvlcMicVol is the SOLE authority for mic gain. Default 100.
  // The old hardcoded setVolume(100) useEffect is gone.
  const [pvlcMicVol,     setPvlcMicVol]     = useState(100);
  const [pvlcPipOn,      setPvlcPipOn]      = useState(true);
  const [pvlcPublishing, setPvlcPublishing] = useState(false);
  const [pvlcError,      setPvlcError]      = useState<string | null>(null);

  /* ── flying emoji reactions ───────────────────────────────────────────── */
  const [flyingEmojis, setFlyingEmojis] = useState<{ id: number; emoji: string; x: number }[]>([]);

  /* ── refs ─────────────────────────────────────────────────────────────── */
  const fullscreenRef     = useRef<HTMLDivElement>(null);
  const rtmClientRef      = useRef<ReturnType<typeof AgoraRTM.createInstance> | null>(null);
  const rtmChannelRef     = useRef<ReturnType<ReturnType<typeof AgoraRTM.createInstance>["createChannel"]> | null>(null);
  const audioMixRef       = useRef<AudioMixUpdatedRef>(null);
  const { result: lyricsResult, fetchForFilename, confirmResult, reset: resetLyrics } = useLyricsAutoFetch();
  const audioCtxDeviceRef = useRef<AudioContext | null>(null);
  const cameraTogglingRef = useRef(false);
  const screenTrackRef    = useRef<any | null>(null);
  const screenPrevCameraOnRef = useRef<boolean>(false);
  const emojiIdRef        = useRef(0);

  // stale-closure-safe refs for RTM callbacks
  const lyricsTimeRef       = useRef(0);
  const lyricsLrcRef        = useRef("");
  const songTitleRef        = useRef("");
  const karaokeFilenameRef  = useRef("");
  const karaokeDurationRef  = useRef(0);
  const isKaraokePlayingRef = useRef(false);
  const karaokeLoadRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKaraokeLoadKeyRef = useRef<string>("");

  // PVLC snapshot refs — updated exclusively via pvlcSendRTM (B6 FIX)
  const pvlcActiveRef   = useRef(false);
  const pvlcTitleRef    = useRef("");
  const pvlcDurationRef = useRef(0);
  const pvlcTimeRef     = useRef(0);
  const pvlcPlayingRef  = useRef(false);

  // B1 FIX: pvlcPhaseRef mirrors pvlcPhase so the camera re-publish timeout
  // reads the current value, not a stale closure-captured value.
  const pvlcPhaseRef = useRef<PvlcPhase>("idle");
  useEffect(() => { pvlcPhaseRef.current = pvlcPhase; }, [pvlcPhase]);

  // PVLC DOM + Agora track refs
  const pvlcVideoRef    = useRef<HTMLVideoElement>(null);
  const pvlcCanvasRef   = useRef<HTMLCanvasElement>(null);
  const pvlcCamVideoRef = useRef<HTMLVideoElement>(null);
  const pvlcRafRef      = useRef<number>(0);
  const pvlcSyncRef     = useRef<ReturnType<typeof setInterval>>();
  const pvlcAudioCtxRef = useRef<AudioContext | null>(null);
  const pvlcAudioSrcRef = useRef<MediaElementAudioSourceNode | null>(null);
  const pvlcCustomVidRef = useRef<any>(null);
  const pvlcCustomAudRef = useRef<any>(null);
  const pvlcPipOnRef    = useRef(true);

  useEffect(() => { pvlcPipOnRef.current = pvlcPipOn; }, [pvlcPipOn]);

  /* ══════════════════════════════════════════════════════════════════════
   * AGORA RTC
   * ══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!userId || !videoId) return;
    getAgoraToken(videoId, userId).then(setToken).catch(console.error);
  }, [videoId, userId]);

  useJoin(
    { appid: AGORA_APP_ID!, channel: videoId, token: token!, uid: userId },
    !!token && !!userId,
  );

  const { localMicrophoneTrack } = useLocalMicrophoneTrack(true, {
    encoderConfig: "high_quality_stereo",
    AEC: false, AGC: false, ANS: false,
  });
  const { localCameraTrack } = useLocalCameraTrack(true);

  // mic only via hook — camera is fully imperative
  usePublish([localMicrophoneTrack]);

  const remoteUsers = useRemoteUsers();
  const { audioTracks } = useRemoteAudioTracks(remoteUsers);
  useEffect(() => { audioTracks.forEach((t) => t.play()); }, [audioTracks]);

  // B3 FIX: only setOptimizationMode here — volume is governed by pvlcMicVol below
  useEffect(() => {
    if (!localMicrophoneTrack) return;
    (localMicrophoneTrack as any).setOptimizationMode?.("music");
  }, [localMicrophoneTrack]);

  // B3 FIX: pvlcMicVol is the single authority for mic gain.
  // Defaults to 100 on mount; reset to 100 in pvlcClose() after PVLC ends.
  useEffect(() => {
    localMicrophoneTrack?.setVolume(pvlcMicVol);
  }, [pvlcMicVol, localMicrophoneTrack]);

  useEffect(() => {
    if (!agoraClient) return;
    let warned = false;
    const h = (s: { uplinkNetworkQuality: number }) => {
      if (s.uplinkNetworkQuality >= 4 && !warned) {
        warned = true;
        enqueueSnackbar("⚠️ Poor connection — viewers may hear audio drops", { variant: "warning", autoHideDuration: 5000 });
      } else if (s.uplinkNetworkQuality <= 2 && warned) {
        warned = false;
        enqueueSnackbar("✅ Connection restored", { variant: "success", autoHideDuration: 3000 });
      }
    };
    agoraClient.on("network-quality", h);
    return () => { agoraClient.off("network-quality", h); };
  }, [agoraClient]);

  /* ── Camera encoder config — Agora live-broadcast recommended profile ────
   * Agora docs recommend 640×480 @ 15fps @ 800kbps as the HIGH stream for
   * live-broadcast on macOS/Windows when dual-stream is enabled.
   * Using 1280×720 @ 3000kbps with dual-stream doubles the CPU encoding load
   * on the talent's machine. 640×480 is still crisp for karaoke performance.
   *
   * bitrateMin: 150 — without a floor Agora defaults to ~400–600kbps minimum.
   * If the talent's uplink can't sustain that, Agora freezes instead of
   * degrading. With 150kbps as the floor it keeps sending a degraded stream.
   * ─────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!localCameraTrack) return;
    localCameraTrack.setEncoderConfiguration({
      width:      640,   // Agora live-broadcast recommended high stream
      height:     480,
      frameRate:  15,    // 15fps recommended for live-broadcast dual-stream mode
      bitrateMax: 800,   // 800kbps — Agora docs: macOS/Windows live-broadcast high stream
      bitrateMin: 150,   // floor — degrades gracefully instead of freezing
    }).catch(() => {});
  }, [localCameraTrack]);

  /* ── Dual-stream mode ─────────────────────────────────────────────────────
   * Talent publishes TWO streams simultaneously (Agora encodes both):
   *
   *   High stream: 640×480  @ 15fps @ 800kbps  ← camera encoder config above
   *   Low  stream: 320×180  @  7fps @ 126kbps  ← setLowStreamParameter below
   *
   * Values match Agora's recommended profile for live-broadcast on macOS/Windows
   * (from the Agora dual-stream docs table: channel profile Live-broadcast,
   * device macOS/Windows).
   *
   * Audience clients receive the high stream by default. When their downlink
   * degrades (network-quality score ≥ 3), Sing4YouLiveStream switches them to
   * the low stream via setRemoteVideoStreamType(uid, 1). On recovery (≤ 2)
   * it switches back to high.
   *
   * enableDualStream() must be called after CONNECTED + camera published.
   * The camera publish effect (300ms delay) runs first — timing is safe.
   * PVLC: skipped — PVLC publishes custom canvas tracks, not the camera.
   * ─────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!agoraClient || agoraClient.connectionState !== "CONNECTED") return;
    if (pvlcPhase === "live") return;

    agoraClient.enableDualStream().catch(() => {});

    // Agora live-broadcast recommended profile — macOS/Windows low stream
    // From docs table: 320×180, 7fps, 126kbps
    agoraClient.setLowStreamParameter({
      width:     320,
      height:    180,
      framerate: 7,     // 7fps — Agora docs recommendation for live-broadcast low stream
      bitrate:   126,   // 126kbps — Agora docs: macOS/Windows live-broadcast low stream
    });
  }, [agoraClient, pvlcPhase]);

  /* ── Camera publish / re-publish after PVLC ends ─────────────────────────
   * B1 FIX: the timeout now reads pvlcPhaseRef.current (always current)
   *         instead of the closure-captured pvlcPhase (stale at callback time).
   *         Delay raised to 300ms so Agora's internal state fully settles after
   *         the custom track unpublish before we try to publish the camera.
   * ─────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!localCameraTrack || !token) return;
    if (agoraClient?.connectionState !== "CONNECTED") return;
    if (pvlcPhase === "live") return;

    const t = setTimeout(() => {
      // B1 FIX: read the ref — never the closure
      if (pvlcPhaseRef.current === "live") return;
      agoraClient.publish([localCameraTrack]).catch((err: any) => {
        if (!/already published/i.test(err?.message ?? ""))
          console.warn("[Sing4You] Camera publish error:", err);
      });
    }, 300); // B1 FIX: raised from 200ms → 300ms
    return () => clearTimeout(t);
  }, [localCameraTrack, token, agoraClient, pvlcPhase]);

  /* ── PVLC: camera feed into hidden video for canvas PiP ──────────────── */
  useEffect(() => {
    if (!localCameraTrack || !pvlcCamVideoRef.current) return;
    const raw = (localCameraTrack as any).getMediaStreamTrack?.() as MediaStreamTrack | undefined;
    if (!raw) return;
    pvlcCamVideoRef.current.srcObject = new MediaStream([raw]);
    pvlcCamVideoRef.current.play().catch(() => {});
  }, [localCameraTrack, pvlcOpen]);

  /* ── PVLC video volume ────────────────────────────────────────────────── */
  useEffect(() => {
    if (pvlcVideoRef.current) pvlcVideoRef.current.volume = pvlcVideoVol / 100;
  }, [pvlcVideoVol]);

  /* ── PVLC video element events ────────────────────────────────────────── */
  useEffect(() => {
    const vid = pvlcVideoRef.current;
    if (!vid) return;
    const onMeta  = () => { setPvlcDuration(vid.duration ?? 0); pvlcDurationRef.current = vid.duration ?? 0; };
    const onTime  = () => { setPvlcTime(vid.currentTime); pvlcTimeRef.current = vid.currentTime; };
    const onPlay  = () => { setPvlcPlaying(true);  pvlcPlayingRef.current = true; };
    const onPause = () => { setPvlcPlaying(false); pvlcPlayingRef.current = false; };
    // B6 FIX: ended event goes through pvlcBroadcastSync which calls pvlcSendRTM
    const onEnded = () => { setPvlcPlaying(false); pvlcPlayingRef.current = false; pvlcBroadcastSync(); };
    const onErr   = () => setPvlcError("Video failed to load. Check the URL or try another file.");
    vid.addEventListener("loadedmetadata", onMeta);
    vid.addEventListener("timeupdate",     onTime);
    vid.addEventListener("play",           onPlay);
    vid.addEventListener("pause",          onPause);
    vid.addEventListener("ended",          onEnded);
    vid.addEventListener("error",          onErr);
    return () => {
      vid.removeEventListener("loadedmetadata", onMeta);
      vid.removeEventListener("timeupdate",     onTime);
      vid.removeEventListener("play",           onPlay);
      vid.removeEventListener("pause",          onPause);
      vid.removeEventListener("ended",          onEnded);
      vid.removeEventListener("error",          onErr);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── PVLC canvas render loop ─────────────────────────────────────────── */
  const startPvlcCanvas = useCallback(() => {
    const canvas = pvlcCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const vid = pvlcVideoRef.current;
    const cam = pvlcCamVideoRef.current;

    const draw = () => {
      // skip drawing entirely if video not ready — saves CPU
      if (vid && vid.readyState >= 1) {
        ctx.drawImage(vid, 0, 0, PVLC_W, PVLC_H);
      } else {
        ctx.fillStyle = "#0c1128";
        ctx.fillRect(0, 0, PVLC_W, PVLC_H);
      }

      if (pvlcPipOnRef.current && cam && cam.readyState >= 2) {
        const px = PVLC_W - PVLC_PIP_W - PVLC_PIP_M;
        const py = PVLC_H - PVLC_PIP_H - PVLC_PIP_M;
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.7)";
        ctx.shadowBlur  = 18;
        ctx.beginPath();
        ctx.moveTo(px + PVLC_PIP_R, py);
        ctx.lineTo(px + PVLC_PIP_W - PVLC_PIP_R, py);
        ctx.quadraticCurveTo(px + PVLC_PIP_W, py, px + PVLC_PIP_W, py + PVLC_PIP_R);
        ctx.lineTo(px + PVLC_PIP_W, py + PVLC_PIP_H - PVLC_PIP_R);
        ctx.quadraticCurveTo(px + PVLC_PIP_W, py + PVLC_PIP_H, px + PVLC_PIP_W - PVLC_PIP_R, py + PVLC_PIP_H);
        ctx.lineTo(px + PVLC_PIP_R, py + PVLC_PIP_H);
        ctx.quadraticCurveTo(px, py + PVLC_PIP_H, px, py + PVLC_PIP_H - PVLC_PIP_R);
        ctx.lineTo(px, py + PVLC_PIP_R);
        ctx.quadraticCurveTo(px, py, px + PVLC_PIP_R, py);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(cam, px, py, PVLC_PIP_W, PVLC_PIP_H);
        ctx.restore();
        ctx.save();
        ctx.strokeStyle = "rgba(255,107,157,0.85)";
        ctx.lineWidth   = 2;
        ctx.beginPath();
        ctx.roundRect?.(px, py, PVLC_PIP_W, PVLC_PIP_H, PVLC_PIP_R);
        ctx.stroke();
        ctx.fillStyle = "rgba(0,0,0,0.65)";
        ctx.font      = "bold 12px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("you", px + PVLC_PIP_W / 2, py + PVLC_PIP_H - 6);
        ctx.restore();
      }

      ctx.save();
      ctx.fillStyle = "rgba(255,107,157,0.9)";
      ctx.font      = "bold 22px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("● PVLC LIVE", 24, 44);
      ctx.restore();

      pvlcRafRef.current = requestAnimationFrame(draw);
    };
    pvlcRafRef.current = requestAnimationFrame(draw);
  }, []);

  const stopPvlcCanvas = useCallback(() => {
    cancelAnimationFrame(pvlcRafRef.current);
    pvlcRafRef.current = 0;
  }, []);

  useEffect(() => {
    if (pvlcPhase === "loaded" || pvlcPhase === "live") {
      startPvlcCanvas();
      return () => stopPvlcCanvas();
    }
    if (pvlcPhase === "idle") stopPvlcCanvas();
  }, [pvlcPhase, startPvlcCanvas, stopPvlcCanvas]);

  /* ── PVLC: the single RTM gateway for all pvlc_ messages ─────────────────
   * B6 FIX: ALL pvlc_ RTM messages must go through pvlcSendRTM so the snapshot
   * refs (pvlcTitleRef, pvlcTimeRef, pvlcPlayingRef, pvlcDurationRef) are always
   * up to date for state_request replies. Direct sendRTM calls for pvlc_ messages
   * are forbidden — this function is the only entry point.
   * ─────────────────────────────────────────────────────────────────────── */
  const pvlcSendRTM = useCallback((msg: object) => {
    const m = msg as { type?: string; title?: string; duration?: number; time?: number; playing?: boolean };
    // Update snapshot refs BEFORE broadcasting so state_request always sees fresh values
    if (m.type === "pvlc_load") {
      pvlcActiveRef.current = true;
      if (m.title    !== undefined) pvlcTitleRef.current    = m.title;
      if (m.duration !== undefined) pvlcDurationRef.current = m.duration;
    } else if (m.type === "pvlc_sync") {
      if (m.time     !== undefined) pvlcTimeRef.current     = m.time;
      if (m.playing  !== undefined) pvlcPlayingRef.current  = m.playing;
      if (m.duration !== undefined) pvlcDurationRef.current = m.duration;
    } else if (m.type === "pvlc_end") {
      pvlcActiveRef.current = false;
    }
    sendRTM(rtmChannelRef, msg);
  }, []);

  /* ── PVLC RTM sync — B6 FIX: calls pvlcSendRTM, not sendRTM directly ── */
  const pvlcBroadcastSync = useCallback(() => {
    const vid = pvlcVideoRef.current;
    if (!vid) return;
    pvlcSendRTM({
      type:     "pvlc_sync",
      time:     vid.currentTime,
      playing:  !vid.paused,
      duration: vid.duration ?? 0,
    });
  }, [pvlcSendRTM]);

  /* ── PVLC: load from file ─────────────────────────────────────────────── */
  // B6 FIX: uses pvlcSendRTM (not sendRTM directly)
  const pvlcLoadFile = useCallback((file: File) => {
    const vid = pvlcVideoRef.current;
    if (!vid) return;
    setPvlcError(null);
    vid.src = URL.createObjectURL(file);
    vid.load();
    const title = file.name.replace(/\.[^.]+$/, "");
    setPvlcVideoTitle(title);
    setPvlcPhase("loaded");
    setPvlcPlaying(false);
    setPvlcTime(0);
    pvlcSendRTM({ type: "pvlc_load", title, duration: 0 });
  }, [pvlcSendRTM]);

  /* ── PVLC: load from URL ──────────────────────────────────────────────── */
  // B6 FIX: uses pvlcSendRTM
  const pvlcLoadUrl = useCallback(() => {
    const vid = pvlcVideoRef.current;
    if (!vid || !pvlcUrlInput.trim()) return;
    setPvlcError(null);
    vid.src = pvlcUrlInput.trim();
    vid.load();
    const title = pvlcUrlInput.split("/").pop() ?? "Live Video";
    setPvlcVideoTitle(title);
    setPvlcPhase("loaded");
    setPvlcPlaying(false);
    setPvlcTime(0);
    setPvlcShowUrl(false);
    pvlcSendRTM({ type: "pvlc_load", title, duration: 0 });
  }, [pvlcUrlInput, pvlcSendRTM]);

  /* ── PVLC: Go Live ────────────────────────────────────────────────────────
   * B2 FIX: createCustomVideoTrack no longer passes encoderConfig.
   *         The canvas resolution (via captureStream) drives the stream.
   * B5 FIX: try/catch around AudioContext creation — both refs nulled on error.
   * B6 FIX: pvlc_load sent via pvlcSendRTM after successful go-live.
   * ─────────────────────────────────────────────────────────────────────── */
  const pvlcGoLive = useCallback(async () => {
    const vid    = pvlcVideoRef.current;
    const canvas = pvlcCanvasRef.current;
    if (!vid || !canvas || !agoraClient) return;

    setPvlcPublishing(true);
    setPvlcError(null);

    try {
      // 1. Canvas stream → Agora custom video track
      //    B2 FIX: no encoderConfig — canvas resolution drives the stream
      const canvasStream     = (canvas as any).captureStream(PVLC_FPS) as MediaStream;
      const canvasVideoTrack = canvasStream.getVideoTracks()[0];
      if (!canvasVideoTrack) throw new Error("Canvas captureStream returned no video track.");

      const agoraVid = AgoraRTC.createCustomVideoTrack({
        mediaStreamTrack: canvasVideoTrack,
        // B2 FIX: encoderConfig block removed — it caused a type mismatch
        // with agora-rtc-react's createCustomVideoTrack signature and prevented
        // the track from being published. The canvas resolution is already
        // correct (PVLC_W × PVLC_H) via captureStream.
      });

      // 2. Video element audio → AudioContext → Agora custom audio track
      //    B5 FIX: try/catch with ref cleanup on failure
      if (!pvlcAudioSrcRef.current) {
        try {
          const ctx = new AudioContext();
          pvlcAudioCtxRef.current = ctx;
          const src = ctx.createMediaElementSource(vid);
          src.connect(ctx.destination); // local playback
          pvlcAudioSrcRef.current = src;
        } catch (audioErr) {
          // B5 FIX: null both refs so a retry won't hit "already used"
          pvlcAudioCtxRef.current = null;
          pvlcAudioSrcRef.current = null;
          throw audioErr;
        }
      }
      const dest = pvlcAudioCtxRef.current!.createMediaStreamDestination();
      pvlcAudioSrcRef.current!.connect(dest);
      const videoAudioTrack = dest.stream.getAudioTracks()[0];
      if (!videoAudioTrack) throw new Error("No audio track from video element.");

      const agoraAud = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: videoAudioTrack });

      pvlcCustomVidRef.current = agoraVid;
      pvlcCustomAudRef.current = agoraAud;

      // 3. Unpublish camera — direct ref, no prop boundary
      if (localCameraTrack) {
        await agoraClient.unpublish([localCameraTrack]).catch(() => {});
      }
      await new Promise((r) => setTimeout(r, 150));

      // 4. Publish canvas video + video audio (mic stays published separately)
      await agoraClient.publish([agoraVid, agoraAud]);

      // 5. Start video playback
      await vid.play();

      // 6. Periodic RTM sync — B6 FIX: via pvlcSendRTM
      pvlcSyncRef.current = setInterval(pvlcBroadcastSync, 1000);

      setPvlcPhase("live");
      pvlcActiveRef.current = true;

      // B6 FIX: broadcast pvlc_load via pvlcSendRTM so snapshot refs are updated
      pvlcSendRTM({
        type:     "pvlc_load",
        title:    pvlcVideoTitle,
        duration: pvlcDurationRef.current,
      });
    } catch (err: any) {
      console.error("[PVLC] goLive failed:", err);
      setPvlcError(err?.message ?? "Failed to start PVLC. Try again.");
      stopPvlcCanvas();
    } finally {
      setPvlcPublishing(false);
    }
  }, [agoraClient, localCameraTrack, stopPvlcCanvas, pvlcBroadcastSync, pvlcSendRTM, pvlcVideoTitle]);

  /* ── PVLC: Stop ──────────────────────────────────────────────────────────
   * B6 FIX: pvlc_end sent via pvlcSendRTM
   * ─────────────────────────────────────────────────────────────────────── */
  const pvlcStop = useCallback(async () => {
    stopPvlcCanvas();
    clearInterval(pvlcSyncRef.current);
    pvlcVideoRef.current?.pause();

    const toUnpublish = [pvlcCustomVidRef.current, pvlcCustomAudRef.current].filter(Boolean) as any[];
    if (toUnpublish.length) await (agoraClient as any).unpublish(toUnpublish).catch(() => {});
    pvlcCustomVidRef.current?.close();
    pvlcCustomAudRef.current?.close();
    pvlcCustomVidRef.current = null;
    pvlcCustomAudRef.current = null;

    await pvlcAudioCtxRef.current?.close().catch(() => {});
    pvlcAudioCtxRef.current = null;
    pvlcAudioSrcRef.current = null;

    // B6 FIX: pvlc_end through pvlcSendRTM — sets pvlcActiveRef.current = false
    pvlcSendRTM({ type: "pvlc_end" });

    // pvlcPhase → "loaded" triggers the camera re-publish useEffect (B1 FIX)
    setPvlcPhase("loaded");
    setPvlcPlaying(false);
  }, [agoraClient, stopPvlcCanvas, pvlcSendRTM]);

  /* ── PVLC: Close panel ────────────────────────────────────────────────── */
  const pvlcClose = useCallback(async () => {
    if (pvlcPhase === "live") await pvlcStop();
    setPvlcPhase("idle");
    setPvlcVideoTitle("");
    setPvlcUrlInput("");
    setPvlcShowUrl(false);
    setPvlcError(null);
    setPvlcTime(0);
    setPvlcDuration(0);
    pvlcActiveRef.current = false;
    // B3 FIX: reset mic vol to 100 so normal mode starts at full gain
    setPvlcMicVol(100);
    setPvlcOpen(false);
  }, [pvlcPhase, pvlcStop]);

  /* ── PVLC: playback controls ─────────────────────────────────────────── */
  // B6 FIX: all use pvlcBroadcastSync which calls pvlcSendRTM
  const pvlcTogglePlay = useCallback(() => {
    const vid = pvlcVideoRef.current;
    if (!vid) return;
    if (vid.paused) vid.play(); else vid.pause();
    pvlcBroadcastSync();
  }, [pvlcBroadcastSync]);

  const pvlcSeek = useCallback((_: Event, val: number | number[]) => {
    const vid = pvlcVideoRef.current;
    if (!vid) return;
    vid.currentTime = val as number;
    setPvlcTime(val as number);
    pvlcBroadcastSync();
  }, [pvlcBroadcastSync]);

  const pvlcSkip = useCallback((secs: number) => {
    const vid = pvlcVideoRef.current;
    if (!vid) return;
    vid.currentTime = Math.max(0, Math.min(vid.duration || 0, vid.currentTime + secs));
    pvlcBroadcastSync();
  }, [pvlcBroadcastSync]);

  /* ══════════════════════════════════════════════════════════════════════
   * AGORA RTM
   * ══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!AGORA_RTM_APP_ID || !userId || !videoId) return;
    let mounted = true;

    const setup = async () => {
      try {
        // Fetch RTM token — required when Agora Console has token auth enabled.
        // If your project uses static key mode (token auth OFF), getAgoraRtmToken
        // can return null and the login will succeed without a token.
        const rtmToken = await getAgoraRtmToken(userId).catch(() => null);

        const client = AgoraRTM.createInstance(AGORA_RTM_APP_ID);
        try {
          await client.login({ uid: userId, token: rtmToken ?? undefined });
        } catch (loginErr: any) {
          const code = loginErr?.code ?? loginErr?.errorCode;
          if (code === 5) {
            // RTM token auth is enabled in Agora Console but no token was provided.
            // Fix: implement getAgoraRtmToken on your backend, or disable RTM
            // token authentication in Agora Console → Project → Edit → RTM.
            console.error("[Sing4You Talent RTM] Token auth required (code 5). Enable getAgoraRtmToken or disable RTM token auth in Agora Console.");
            enqueueSnackbar("Chat sync unavailable — RTM token required. Contact support.", { variant: "warning", autoHideDuration: 6000 });
          }
          throw loginErr;
        }
        if (!mounted) { client.logout().catch(() => {}); return; }

        const channel = client.createChannel(videoId);
        await channel.join();

        const updateCount = async () => {
          try { const m = await channel.getMembers(); if (mounted) setViewerCount(m.length); } catch {}
        };

        const broadcastKaraokeSnapshot = () => {
          // Align with Cafe PVLC page: send title/artist (no full LRC) so viewers
          // can fetch lyrics from LRCLIB. RTM has a 32KB message limit.
          const combined = (songTitleRef.current ?? "").trim();
          const splitEmDash = combined.split(" — ");
          const splitDash = splitEmDash.length >= 2 ? splitEmDash : combined.split(" - ");
          const title = (splitDash[0] ?? "").trim();
          const artist = (splitDash.slice(1).join(" - ") ?? "").trim();
          const filename = (karaokeFilenameRef.current ?? "").trim();

          if (title || artist) {
            console.debug("[Sing4You Talent → RTM] karaoke_load (snapshot)", {
              title,
              artist,
              duration: karaokeDurationRef.current,
              time: lyricsTimeRef.current,
            });
            sendRTM(rtmChannelRef, {
              type: "karaoke_load",
              title,
              artist,
              duration: karaokeDurationRef.current,
              time: lyricsTimeRef.current,
            });
          } else if (filename) {
            // Fallback: viewers can still do LRCLIB search by filename if no tags yet
            console.debug("[Sing4You Talent → RTM] karaoke_track (snapshot)", {
              filename,
              duration: karaokeDurationRef.current,
              time: lyricsTimeRef.current,
            });
            sendRTM(rtmChannelRef, {
              type: "karaoke_track",
              filename,
              duration: karaokeDurationRef.current,
              time: lyricsTimeRef.current,
            });
          }

          setTimeout(
            () => sendRTM(rtmChannelRef, { type: "karaoke_sync", time: lyricsTimeRef.current, playing: isKaraokePlayingRef.current }),
            400,
          );
        };

        // IMPORTANT: RTM may finish joining after LRCLIB already loaded lyrics/title.
        // Re-broadcast immediately on join so viewers always receive the snapshot.
        // (This avoids the "karaoke_load fired before RTM ready" silent drop.)
        try {
          if (lyricsLrcRef.current || songTitleForRTM || karaokeFilenameRef.current) {
            console.debug("[Sing4You Talent → RTM] joined; broadcasting karaoke snapshot");
            broadcastKaraokeSnapshot();
          }
        } catch {
          // ignore
        }

        channel.on("MemberJoined", () => { updateCount(); broadcastKaraokeSnapshot(); });
        channel.on("MemberLeft",   updateCount);
        updateCount();

        channel.on("ChannelMessage", (message: any) => {
          try {
            const msg = JSON.parse(message.text ?? "{}");

            if (msg.type === "reaction" && msg.emoji && mounted) {
              const id = ++emojiIdRef.current;
              setFlyingEmojis((prev) => [...prev, { id, emoji: msg.emoji, x: 10 + Math.random() * 70 }]);
              setTimeout(() => setFlyingEmojis((prev) => prev.filter((e) => e.id !== id)), 2500);
            }

            if (msg.type === "state_request") {
              broadcastKaraokeSnapshot();
              // B6 FIX: pvlcActiveRef / pvlcTitleRef / pvlcTimeRef are always
              // fresh because ALL pvlc_ messages go through pvlcSendRTM
              if (pvlcActiveRef.current) {
                sendRTM(rtmChannelRef, { type: "pvlc_load", title: pvlcTitleRef.current, duration: pvlcDurationRef.current });
                sendRTM(rtmChannelRef, { type: "pvlc_sync", time: pvlcTimeRef.current, playing: pvlcPlayingRef.current, duration: pvlcDurationRef.current });
              }
            }
          } catch {}
        });

        rtmClientRef.current  = client;
        rtmChannelRef.current = channel;
      } catch (err) { console.warn("[Sing4You RTM]", err); }
    };

    setup();
    return () => {
      mounted = false;
      rtmChannelRef.current?.leave().catch(() => {});
      rtmClientRef.current?.logout().catch(() => {});
    };
  }, [userId, videoId]);

  /* ── stale-closure-safe ref syncs ────────────────────────────────────── */
  useEffect(() => { lyricsTimeRef.current       = lyricsTime;       }, [lyricsTime]);
  useEffect(() => { lyricsLrcRef.current        = lyricsLrc;        }, [lyricsLrc]);
  useEffect(() => { songTitleRef.current        = songTitle;        }, [songTitle]);
  useEffect(() => { karaokeFilenameRef.current  = karaokeFilename;  }, [karaokeFilename]);
  useEffect(() => { karaokeDurationRef.current  = karaokeDuration;  }, [karaokeDuration]);
  useEffect(() => { isKaraokePlayingRef.current = isKaraokePlaying; }, [isKaraokePlaying]);

  // Cleanup any pending karaoke_load retry timers
  useEffect(() => {
    return () => {
      if (karaokeLoadRetryRef.current) clearTimeout(karaokeLoadRetryRef.current);
    };
  }, []);

  /* ── Auto-inject lyrics when LRCLIB fetch completes ─────────────────────
   * "confirm" status is handled by the LyricsStatusBadge confirm button.
   * Only "found" and "plain" (high-confidence) trigger auto-injection.
   * When the talent uploads an MP3, useLyricsAutoFetch searches LRCLIB and
   * populates lyricsResult. This effect watches for a completed result
   * (found or plain) and injects the LRC directly — talent does nothing.
   * If the talent has already manually loaded lyrics (lyricsLrc !== ""),
   * the auto-result is ignored so we never overwrite intentional choices.
   * ─────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (lyricsResult.status !== "found" && lyricsResult.status !== "plain") return;
    if (!lyricsResult.lrc) return;
    // Don't overwrite if talent already has lyrics loaded manually
    if (lyricsLrc) return;
    // Ensure RTM metadata is set before karaoke_load broadcast runs
    setSongTitleForRTM((lyricsResult.title ?? "").trim());
    setSongArtistForRTM((lyricsResult.artist ?? "").trim());
    handleLyricsChange(lyricsResult.lrc);
  }, [lyricsResult.status, lyricsResult.lrc]); // eslint-disable-line react-hooks/exhaustive-deps

  // User-friendly toasts for lyrics lookup result
  useEffect(() => {
    if (lyricsResult.status === "found" || lyricsResult.status === "plain") {
      enqueueSnackbar("Lyrics loaded automatically for this track.", {
        variant: "success",
        autoHideDuration: 3500,
      });
    } else if (lyricsResult.status === "not_found") {
      enqueueSnackbar("No lyrics found online — you can upload a .lrc file in the Karaoke Studio.", {
        variant: "warning",
        autoHideDuration: 5000,
      });
    } else if (lyricsResult.status === "error") {
      enqueueSnackbar("Lyrics lookup failed — check your connection or upload a .lrc file.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  }, [lyricsResult.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // When MP3 is loaded, LRCLIB lookup often returns title/artist before we have LRC.
  // Populate songTitle early so UI + RTM viewers can show "Title — Artist" immediately.
  useEffect(() => {
    const title = (lyricsResult.title ?? "").trim();
    const artist = (lyricsResult.artist ?? "").trim();
    if (!title && !artist) return;
    if (songTitle) return;
    console.debug("[Sing4You Talent] LRCLIB metadata resolved", {
      title,
      artist,
      status: lyricsResult.status,
      filename: karaokeFilenameRef.current,
      query: lyricsResult.query,
      confidence: lyricsResult.confidence,
    });
    setSongTitle(title && artist ? `${title} — ${artist}` : title || artist);
    // Note: RTM broadcast is handled by the karaoke_load effect on [songTitle, karaokeDuration].
    // This ensures we don't broadcast on every intermediate LRCLIB status update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lyricsResult.title, lyricsResult.artist]);

  /* ── karaoke RTM broadcasts ───────────────────────────────────────────── */
  useEffect(() => {
    // Align with Cafe PVLC page: send title/artist (no full LRC) so viewers
    // can fetch lyrics from LRCLIB. RTM has a 32KB message limit.
    if (!songTitleForRTM) return;
    const title = songTitleForRTM.trim();
    const artist = songArtistForRTM.trim();
    const payload = { type: "karaoke_load", title, artist, duration: karaokeDuration, time: 0 };
    const key = `${title}||${artist}||${karaokeDuration}`;

    const sendNow = () => {
      // Avoid spamming the same snapshot repeatedly
      if (lastKaraokeLoadKeyRef.current === key) return;
      lastKaraokeLoadKeyRef.current = key;
      console.debug("[Sing4You Talent → RTM] karaoke_load", payload);
      sendRTM(rtmChannelRef, payload);
    };

    // Send immediately (best effort)
    sendNow();

    // Retry after 2s to catch the case where RTM channel was still joining.
    if (karaokeLoadRetryRef.current) clearTimeout(karaokeLoadRetryRef.current);
    karaokeLoadRetryRef.current = setTimeout(() => {
      console.debug("[Sing4You Talent → RTM] karaoke_load retry (+2s)", payload);
      sendRTM(rtmChannelRef, payload);
    }, 2000);
  }, [lyricsLrc, songTitleForRTM, songArtistForRTM, karaokeDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const delay = isKaraokePlaying ? 300 : 0;
    // When playback starts, re-broadcast track metadata immediately so viewers
    // can start LRCLIB lookup even if they missed the earlier upload event.
    if (isKaraokePlaying) {
      const combined = (songTitleRef.current ?? "").trim();
      const splitEmDash = combined.split(" — ");
      const splitDash = splitEmDash.length >= 2 ? splitEmDash : combined.split(" - ");
      const title = (splitDash[0] ?? "").trim();
      const artist = (splitDash.slice(1).join(" - ") ?? "").trim();
      const filename = (karaokeFilenameRef.current ?? "").trim();
      if (title || artist) {
        console.debug("[Sing4You Talent → RTM] karaoke_load (on play start)", {
          title,
          artist,
          duration: karaokeDurationRef.current,
          time: lyricsTimeRef.current,
        });
        sendRTM(rtmChannelRef, { type: "karaoke_load", title, artist, duration: karaokeDurationRef.current, time: lyricsTimeRef.current });
      } else if (filename) {
        console.debug("[Sing4You Talent → RTM] karaoke_track (on play start)", {
          filename,
          duration: karaokeDurationRef.current,
          time: lyricsTimeRef.current,
        });
        sendRTM(rtmChannelRef, { type: "karaoke_track", filename, duration: karaokeDurationRef.current, time: lyricsTimeRef.current });
      }
    }
    const bc = () => sendRTM(rtmChannelRef, { type: "karaoke_sync", time: lyricsTimeRef.current, playing: isKaraokePlaying });
    const t1 = setTimeout(bc, delay);
    const t2 = setTimeout(bc, delay + 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isKaraokePlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isKaraokePlaying) return;
    const id = setInterval(() => sendRTM(rtmChannelRef, { type: "karaoke_sync", time: lyricsTimeRef.current, playing: true }), 1000);
    return () => clearInterval(id);
  }, [isKaraokePlaying]);

  /* ══════════════════════════════════════════════════════════════════════
   * FULLSCREEN / MIC / CAMERA
   * ══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const toggleFullscreen = () => {
    if (isFullscreen) document.exitFullscreen().catch(() => {});
    else fullscreenRef.current?.requestFullscreen().catch(() => {});
  };

  const toggleMic = () => {
    localMicrophoneTrack?.setEnabled(!micOn).catch(() => {});
    setMicOn((p) => !p);
  };

  const toggleCamera = useCallback(async () => {
    if (!localCameraTrack || cameraTogglingRef.current) return;
    cameraTogglingRef.current = true;
    try {
      if (cameraOn) {
        if (agoraClient?.connectionState === "CONNECTED") {
          await agoraClient.unpublish([localCameraTrack]).catch(() => {});
        }
        await localCameraTrack.setEnabled(false);
        setCameraOn(false);
      } else {
        await localCameraTrack.setEnabled(true);
        await new Promise((r) => setTimeout(r, 200));
        // B1 FIX: read ref not closure
        if (agoraClient?.connectionState === "CONNECTED" && pvlcPhaseRef.current !== "live") {
          await agoraClient.publish([localCameraTrack]).catch((err: any) => {
            if (!/already published/i.test(err?.message ?? ""))
              console.warn("[Sing4You] Camera re-publish error:", err);
          });
        }
        setCameraOn(true);
      }
    } catch (err) {
      console.warn("[Sing4You] toggleCamera error:", err);
    } finally {
      cameraTogglingRef.current = false;
    }
  }, [localCameraTrack, cameraOn, agoraClient]);

  const toggleScreenShare = useCallback(async () => {
    if (!agoraClient) return;
    if (pvlcPhase === "live") {
      enqueueSnackbar("Stop PVLC before sharing your screen.", {
        variant: "warning",
        autoHideDuration: 4000,
      });
      return;
    }

    try {
      if (screenOn) {
        const track = screenTrackRef.current;
        if (track) {
          if (agoraClient.connectionState === "CONNECTED") {
            await agoraClient.unpublish([track as any]).catch(() => {});
          }
          try {
            track.close?.();
          } catch {
            // ignore
          }
          screenTrackRef.current = null;
        }
        setScreenOn(false);

        // Restore camera only if it was on before screen share started
        if (localCameraTrack && screenPrevCameraOnRef.current) {
          await localCameraTrack.setEnabled(true).catch(() => {});
          if (agoraClient.connectionState === "CONNECTED") {
            await agoraClient.publish([localCameraTrack]).catch((err: any) => {
              if (!/already published/i.test(err?.message ?? "")) {
                console.warn("[Sing4You] Camera re-publish error after screen share:", err);
              }
            });
          }
          setCameraOn(true);
        }
        screenPrevCameraOnRef.current = false;
        return;
      }

      const created: any = await (AgoraRTC as any).createScreenVideoTrack(
        {
          encoderConfig: {
            width: 1280,
            height: 720,
            frameRate: 15,
          },
        },
        "auto",
      );

      const screenTrack = Array.isArray(created) ? created[0] : created;
      if (!screenTrack) return;
      screenTrackRef.current = screenTrack;

      if (agoraClient.connectionState === "CONNECTED") {
        await agoraClient.publish([screenTrack as any]).catch((err: any) => {
          console.warn("[Sing4You] Screen share publish error:", err);
        });
      }

      // Remember whether camera was on before starting screen share
      screenPrevCameraOnRef.current = cameraOn;

      if (localCameraTrack && cameraOn) {
        if (agoraClient.connectionState === "CONNECTED") {
          await agoraClient.unpublish([localCameraTrack]).catch(() => {});
        }
        await localCameraTrack.setEnabled(false).catch(() => {});
        setCameraOn(false);
      }

      try {
        screenTrack.on?.("track-ended", async () => {
          if (!screenTrackRef.current) return;
          if (agoraClient.connectionState === "CONNECTED") {
            await agoraClient.unpublish([screenTrackRef.current as any]).catch(() => {});
          }
          try {
            screenTrackRef.current.close?.();
          } catch {
            // ignore
          }
          screenTrackRef.current = null;
          setScreenOn(false);

          // Restore camera only if it was on before screen share started
          if (localCameraTrack && screenPrevCameraOnRef.current) {
            await localCameraTrack.setEnabled(true).catch(() => {});
            if (agoraClient.connectionState === "CONNECTED") {
              await agoraClient.publish([localCameraTrack]).catch(() => {});
            }
            setCameraOn(true);
          }
          screenPrevCameraOnRef.current = false;
        });
      } catch {
        // ignore
      }

      setScreenOn(true);
    } catch (err) {
      console.warn("[Sing4You] toggleScreenShare error:", err);
      const errorName = (err as any)?.name as string | undefined;
      const errorMessage = String((err as any)?.message ?? "");
      const userCancelled =
        errorName === "NotAllowedError" ||
        errorName === "AbortError" ||
        /Permission denied/i.test(errorMessage) ||
        /The user aborted/i.test(errorMessage) ||
        /The user canceled/i.test(errorMessage) ||
        /denied/i.test(errorMessage);

      if (!userCancelled) {
        enqueueSnackbar("Screen sharing failed. Check browser permissions and try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    }
  }, [agoraClient, cameraOn, localCameraTrack, pvlcPhase, screenOn]);

  /* ══════════════════════════════════════════════════════════════════════
   * DEVICE MANAGEMENT
   * ══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!localMicrophoneTrack) return;
    const loadDevices = async () => {
      try {
        const mics = await AgoraRTC.getMicrophones();
        setMicDevices(mics);
        const cur = (localMicrophoneTrack as any).getTrackLabel?.() as string | undefined;
        const m = mics.find((d) => d.label === cur);
        setActiveMicId(m?.deviceId ?? mics[0]?.deviceId ?? "");
      } catch {}
      try {
        const all  = await navigator.mediaDevices.enumerateDevices();
        const outs = all.filter((d) => d.kind === "audiooutput");
        setOutputDevices(outs);
        const def = outs.find((d) => d.deviceId === "default");
        setActiveOutputId((prev) => prev || def?.deviceId || outs[0]?.deviceId || "");
      } catch {}
    };
    loadDevices();
    navigator.mediaDevices.addEventListener("devicechange", loadDevices);
    AgoraRTC.onMicrophoneChanged = async (c: any) => {
      const mics = await AgoraRTC.getMicrophones();
      setMicDevices(mics);
      if (c.state === "ACTIVE") {
        (localMicrophoneTrack as any).setDevice?.(c.device.deviceId);
        setActiveMicId(c.device.deviceId);
        enqueueSnackbar(`Mic: ${c.device.label || "new device"}`, { variant: "info", autoHideDuration: 3500 });
      } else {
        const lbl = (localMicrophoneTrack as any).getTrackLabel?.() as string | undefined;
        if (c.device.label === lbl && mics[0]) {
          (localMicrophoneTrack as any).setDevice?.(mics[0].deviceId);
          setActiveMicId(mics[0].deviceId);
          enqueueSnackbar(`Mic disconnected → ${mics[0].label || "default"}`, { variant: "warning", autoHideDuration: 4000 });
        }
      }
    };
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", loadDevices);
      AgoraRTC.onMicrophoneChanged = null as any;
    };
  }, [localMicrophoneTrack]);

  useEffect(() => {
    if (!audioQualityOpen || !localMicrophoneTrack) return;
    AgoraRTC.getMicrophones().then(setMicDevices).catch(() => {});
    navigator.mediaDevices.enumerateDevices().then((all) => setOutputDevices(all.filter((d) => d.kind === "audiooutput"))).catch(() => {});
  }, [audioQualityOpen, localMicrophoneTrack]);

  const handleMicDeviceChange = useCallback((deviceId: string) => {
    setActiveMicId(deviceId);
    (localMicrophoneTrack as any)?.setDevice?.(deviceId);
  }, [localMicrophoneTrack]);

  const handleOutputDeviceChange = useCallback(async (deviceId: string) => {
    setActiveOutputId(deviceId);
    try {
      if (!audioCtxDeviceRef.current) audioCtxDeviceRef.current = new AudioContext();
      await (audioCtxDeviceRef.current as any).setSinkId?.(deviceId);
    } catch {}
  }, []);

  /* ══════════════════════════════════════════════════════════════════════
   * AUDIO QUALITY
   * ══════════════════════════════════════════════════════════════════════ */
  const applyEncoderPreset = useCallback((preset: "high_quality_stereo" | "high_quality" | "standard") => {
    const t = localMicrophoneTrack as any;
    if (!t) return;
    if (preset === "high_quality_stereo") {
      t.setAudioEncoderConfiguration?.({ sampleRate: 48000, stereo: true,  bitrate: 128 });
      t.setOptimizationMode?.("music");
    } else if (preset === "high_quality") {
      t.setAudioEncoderConfiguration?.({ sampleRate: 48000, stereo: false, bitrate: 128 });
      t.setOptimizationMode?.("music");
    } else {
      t.setAudioEncoderConfiguration?.({ sampleRate: 32000, stereo: false, bitrate: 64 });
      t.setOptimizationMode?.("default");
    }
  }, [localMicrophoneTrack]);

  const handleToggleAEC = useCallback(() => {
    setAecEnabled((p) => { const n = !p; (localMicrophoneTrack as any)?._mediaStreamTrack?.applyConstraints({ echoCancellation: n }).catch(() => {}); return n; });
  }, [localMicrophoneTrack]);
  const handleToggleAGC = useCallback(() => {
    setAgcEnabled((p) => { const n = !p; (localMicrophoneTrack as any)?._mediaStreamTrack?.applyConstraints({ autoGainControl: n }).catch(() => {}); return n; });
  }, [localMicrophoneTrack]);
  const handleToggleANS = useCallback(() => {
    setAnsEnabled((p) => { const n = !p; (localMicrophoneTrack as any)?._mediaStreamTrack?.applyConstraints({ noiseSuppression: n }).catch(() => {}); return n; });
  }, [localMicrophoneTrack]);

  /* ══════════════════════════════════════════════════════════════════════
   * SESSION
   * ══════════════════════════════════════════════════════════════════════ */
  const cleanupTracks = useCallback(() => {
    localMicrophoneTrack?.close();
    localCameraTrack?.close();

    const screenTrack = screenTrackRef.current;
    if (screenTrack) {
      // Best-effort unpublish + close any active screen-share track when ending/leaving
      if (agoraClient?.connectionState === "CONNECTED") {
        (agoraClient as any).unpublish([screenTrack]).catch(() => {});
      }
      try {
        screenTrack.close?.();
      } catch {
        // ignore
      }
      screenTrackRef.current = null;
      setScreenOn(false);
    }
  }, [localMicrophoneTrack, localCameraTrack, agoraClient]);

  const handleEndStream = useCallback(async () => {
    cleanupTracks();
    try {
      await startEndStageSession.mutateAsync({
        bookingId, status: "end",
        ...(currentBusiness?.id && { business_id: currentBusiness.id.toString() }),
      });
      enqueueSnackbar("Stream ended successfully", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to end stream", { variant: "error" });
    }
    setShowEndDialog(false);
    navigate("/");
  }, [startEndStageSession, bookingId, currentBusiness, navigate, cleanupTracks]);

  const handleLeave = useCallback(() => { cleanupTracks(); navigate("/"); }, [navigate, cleanupTracks]);

  // Ensure any active screen-share track is cleaned up if the component unmounts
  useEffect(() => {
    return () => {
      const screenTrack = screenTrackRef.current;
      if (!screenTrack) return;
      if (agoraClient?.connectionState === "CONNECTED") {
        (agoraClient as any).unpublish([screenTrack]).catch(() => {});
      }
      try {
        screenTrack.close?.();
      } catch {
        // ignore
      }
      screenTrackRef.current = null;
    };
  }, [agoraClient]);

  /* ══════════════════════════════════════════════════════════════════════
   * CREATOR TOOLS
   * ══════════════════════════════════════════════════════════════════════ */
  const toggleTool = useCallback((id: ToolId) => {
    setActiveTools((p) => p.includes(id) ? p.filter((t) => t !== id) : [...p, id]);
    setToolsOpen(false);
  }, []);
  const removeTool = useCallback((id: ToolId) => setActiveTools((p) => p.filter((t) => t !== id)), []);

  const handleSongAdded = useCallback((song: SongItem) => {
    const url = song?.url != null ? String(song.url).trim() : "";
    if (!url) return;
    setSongList((p) => [...p, { ...song, url }]);
    setActiveTools((p) => p.includes("songlist") ? p : [...p, "songlist"]);
  }, []);

  const handleSongSelect = useCallback((song: SongItem) => {
    if (!song.url) { enqueueSnackbar("This song has no audio URL yet.", { variant: "warning" }); return; }
    setActiveTrackId(song.id);
    setKaraokeFilename(song.name);
    // Early signal to viewers to start LRCLIB lookup before playback starts.
    console.debug("[Sing4You Talent] Song selected (URL)", { filename: song.name, url: song.url });
    console.debug("[Sing4You Talent → RTM] karaoke_track (on select)", { filename: song.name, duration: karaokeDurationRef.current, time: 0 });
    sendRTM(rtmChannelRef, { type: "karaoke_track", filename: song.name, duration: karaokeDurationRef.current, time: 0 });
    resetLyrics();
    fetchForFilename(song.name);
    audioMixRef.current?.loadUrl(song.url, song.name);
    enqueueSnackbar(`Loading "${song.name}" into Karaoke Studio…`, { variant: "info" });
  }, [fetchForFilename, resetLyrics]);

  /* ══════════════════════════════════════════════════════════════════════
   * KARAOKE CALLBACKS
   * ══════════════════════════════════════════════════════════════════════ */
  const handleLyricsChange = useCallback((lrc: string | null) => {
    const text = lrc ?? "";
    setLyricsLrc(text);
    if (text) {
      let max = 0;
      for (const line of text.split(/\r?\n/)) {
        const m = line.match(/\[(\d{1,2}):(\d{1,2})(?:\.(\d{1,2}))?]/);
        if (m) {
          const t = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + parseInt(m[3] ?? "0", 10) / 100;
          if (t > max) max = t;
        }
      }
      setKaraokeDuration((p) => p > 0 ? p : max);
      const title  = text.match(/\[ti:(.+?)\]/i)?.[1]?.trim() ?? "";
      const artist = text.match(/\[ar:(.+?)\]/i)?.[1]?.trim() ?? "";
      setSongTitle(title && artist ? `${title} — ${artist}` : title || artist);
    } else {
      setSongTitle(""); setKaraokeDuration(0);
    }
    setLyricsTime(0);
  }, []);

  const handlePlayPause    = useCallback(() => { if (isKaraokePlaying) audioMixRef.current?.pause(); else audioMixRef.current?.play(); }, [isKaraokePlaying]);
  const handlePrev         = useCallback(() => audioMixRef.current?.stop(), []);
  const handleNext         = useCallback(() => audioMixRef.current?.stop(), []);
  const handleSeek         = useCallback((s: number) => audioMixRef.current?.seekTo(s), []);
  const handleVolumeChange = useCallback((v: number) => { setVolume(v); audioMixRef.current?.setVol(v); }, []);

  // Sidebar tab strip scrolling (when there are more tabs than fit).
  // Must be declared before any early return to keep hooks order stable.
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const scrollTabs = useCallback((dir: "left" | "right") => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const amt = Math.max(120, Math.round(el.clientWidth * 0.7));
    el.scrollBy({ left: dir === "left" ? -amt : amt, behavior: "smooth" });
  }, []);

  if (!userId) return null;
  const displayName = currentProfile?.username ?? userId;

  /* ════════════════════════════════════════════════════════════════════════
   * RENDER
   * ════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="s4y-page" ref={fullscreenRef}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="s4y-header">
        <div className="s4y-logo">
          <span className="s4y-logo-icon">🎤</span>
          <div className="s4y-logo-text">
            <span className="s4y-logo-title">{pvlcOpen ? "PLAY VIDEO LIVE COMMENTARY" : "SING4YOU"}</span>
            <span className="s4y-logo-sub">{pvlcOpen ? "Main: your video · PiP: your camera" : "Live Karaoke"}</span>
          </div>
        </div>
        <div className="s4y-role-badge">🎤&nbsp;&nbsp;TALENT</div>
        <div className="s4y-header-right">
          <button
            className="s4y-tools-btn"
            onClick={() => setSidebarOpen((o) => !o)}
            title={sidebarOpen ? "Hide panel" : "Show panel"}
          >
            <ChatBubbleOutline sx={{ fontSize: "0.9rem" }} />
            {sidebarOpen ? "Hide panel" : "Show panel"}
          </button>
          <span className="s4y-live-pill"><span className="s4y-live-dot" />LIVE</span>
          <span className="s4y-viewer-count"><Visibility sx={{ fontSize: "0.9rem", opacity: 0.65 }} />{viewerCount}</span>
        </div>
      </header>

      <div
        className="s4y-body"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <main className="s4y-main">

          {/* ── Video / PVLC area ────────────────────────────────────────── */}
          <div className="s4y-video-area" style={{ position: "relative", minHeight: "70vh" }}>

            {/* Hidden PVLC DOM elements — always mounted so refs are stable */}
            <video ref={pvlcVideoRef}    style={{ display: "none" }} crossOrigin="anonymous" playsInline />
            <video ref={pvlcCamVideoRef} style={{ display: "none" }} playsInline muted />

            {pvlcOpen ? (
              /* ── PVLC panel ─────────────────────────────────────────── */
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", background: "linear-gradient(160deg,#080d1e 0%,#0d1630 60%,#0a1020 100%)", borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,107,157,0.2)" }}>

                {/* PVLC header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,107,157,0.04)", flexShrink: 0 }}>
                  <MovieFilter sx={{ color: "#ff6b9d", fontSize: "1.1rem" }} />
                  <span style={{ fontWeight: 700, fontSize: "0.88rem", letterSpacing: "0.08em", color: "#fff", textTransform: "uppercase" }}>Play Video Live Commentary</span>
                  {pvlcPhase === "live" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "2px 9px", background: "#ff6b9d", borderRadius: 4 }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", animation: "pvlcPulse 1s ease-in-out infinite" }} />
                      <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.65rem", letterSpacing: 1 }}>LIVE</span>
                    </div>
                  )}
                  <div style={{ flex: 1 }} />
                  {pvlcPhase === "live" && (
                    <Tooltip title="Stop PVLC and return to camera">
                      <button onClick={pvlcStop} style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", background: "transparent", border: "1px solid rgba(255,107,157,0.4)", borderRadius: 6, color: "#ff6b9d", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                        <Stop sx={{ fontSize: "0.85rem" }} /> End PVLC
                      </button>
                    </Tooltip>
                  )}
                  {pvlcPhase !== "live" && (
                    <Tooltip title="Cancel PVLC and return to normal camera view">
                      <button
                        onClick={pvlcClose}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "5px 12px",
                          background: "transparent",
                          border: "1px solid rgba(148,163,184,0.5)",
                          borderRadius: 6,
                          color: "rgba(226,232,240,0.9)",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          cursor: "pointer",
                          marginRight: 8,
                        }}
                      >
                        Cancel
                      </button>
                    </Tooltip>
                  )}
                  <IconButton onClick={pvlcClose} size="small" sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: "#fff" } }}>
                    <Close fontSize="small" />
                  </IconButton>
                </div>

                {/* PVLC body */}
                <div style={{ display: "flex", flex: 1, minHeight: 0 }}>

                  {/* Canvas preview */}
                  <div style={{ flex: 1, position: "relative", background: "#050810", display: "flex", flexDirection: "column", minWidth: 0 }}>
                    {pvlcPhase !== "idle" && (
                      <div style={{ padding: "6px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 1.2 }}>Main: your video</span>
                        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.65rem" }}>·</span>
                        <span style={{ color: "#ff6b9d", fontSize: "0.68rem", fontWeight: 600 }}>PiP: camera</span>
                      </div>
                    )}
                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                      <canvas ref={pvlcCanvasRef} width={PVLC_W} height={PVLC_H} style={{ width: "100%", height: "auto", maxHeight: 380, objectFit: "contain", display: pvlcPhase === "idle" ? "none" : "block" }} />

                      {pvlcPhase === "idle" && (
                        <div
                          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type.startsWith("video/")) pvlcLoadFile(f); }}
                          onDragOver={(e) => e.preventDefault()}
                          onClick={() => document.getElementById("pvlc-file-input")?.click()}
                          style={{ position: "absolute", inset: 10, border: "2px dashed rgba(255,107,157,0.2)", borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, cursor: "pointer" }}
                        >
                          <div style={{ width: 54, height: 54, borderRadius: "50%", background: "rgba(255,107,157,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <CloudUpload sx={{ color: "#ff6b9d", fontSize: "1.6rem" }} />
                          </div>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem" }}>Drop a video file here</div>
                            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", marginTop: 4 }}>MP4, WebM, MOV — or click to browse</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {pvlcPhase !== "idle" && pvlcDuration > 0 && (
                      <div style={{ padding: "6px 14px", background: "rgba(0,0,0,0.25)", flexShrink: 0 }}>
                        <div style={{ position: "relative", height: 3, background: "rgba(255,255,255,0.15)", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pvlcDuration > 0 ? (pvlcTime / pvlcDuration) * 100 : 0}%`, background: "#ff6b9d", borderRadius: 2, transition: "width 0.1s linear" }} />
                        </div>
                      </div>
                    )}

                    <input id="pvlc-file-input" type="file" accept="video/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) pvlcLoadFile(f); e.target.value = ""; }} />
                    {pvlcError && <div style={{ position: "absolute", bottom: 10, left: 10, right: 10, background: "rgba(239,68,68,0.14)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 8, padding: "10px 12px" }}><span style={{ color: "#ef4444", fontSize: "0.76rem" }}>{pvlcError}</span></div>}
                  </div>

                  {/* Controls panel */}
                  <div style={{ width: 270, flexShrink: 0, borderLeft: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", padding: 18, gap: 14, background: "rgba(0,0,0,0.18)", overflowY: "auto" }}>

                    <div>
                      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Now playing</div>
                      <div style={{ color: pvlcVideoTitle ? "#fff" : "rgba(255,255,255,0.35)", fontWeight: 600, fontSize: "0.82rem", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any }}>
                        {pvlcVideoTitle || "No video loaded"}
                      </div>
                    </div>

                    <div>
                      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Load video</div>
                      {pvlcShowUrl ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <input value={pvlcUrlInput} onChange={(e) => setPvlcUrlInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pvlcLoadUrl(); }} placeholder="https://example.com/video.mp4" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 7, padding: "7px 10px", color: "#fff", fontSize: "0.76rem", outline: "none", width: "100%", boxSizing: "border-box" }} />
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={pvlcLoadUrl} style={{ flex: 1, background: "#ff6b9d", border: "none", borderRadius: 6, color: "#fff", fontWeight: 700, fontSize: "0.76rem", padding: "6px", cursor: "pointer" }}>Load</button>
                            <button onClick={() => setPvlcShowUrl(false)} style={{ background: "rgba(255,255,255,0.07)", border: "none", borderRadius: 6, color: "rgba(255,255,255,0.5)", fontSize: "0.76rem", padding: "6px 9px", cursor: "pointer" }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <PvlcFileBtn icon={<CloudUpload sx={{ fontSize: "0.85rem" }} />} label="Load Video File" onClick={() => document.getElementById("pvlc-file-input")?.click()} />
                          <PvlcFileBtn icon={<LinkIcon sx={{ fontSize: "0.85rem" }} />} label="Load from URL" onClick={() => setPvlcShowUrl(true)} muted />
                        </div>
                      )}
                    </div>

                    {pvlcPhase !== "idle" && (
                      <div>
                        <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Playback</div>
                        <Slider value={pvlcTime} min={0} max={pvlcDuration || 1} step={0.5} onChange={pvlcSeek} sx={sliderSx("#ff6b9d")} size="small" />
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2, marginBottom: 8 }}>
                          <span style={{ color: "rgba(255,255,255,0.32)", fontSize: "0.65rem" }}>{fmtTime(pvlcTime)}</span>
                          <span style={{ color: "rgba(255,255,255,0.32)", fontSize: "0.65rem" }}>{fmtTime(pvlcDuration)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
                          <IconButton onClick={() => pvlcSkip(-10)} size="small" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ff6b9d" } }}><SkipPrevious sx={{ fontSize: "1.4rem" }} /></IconButton>
                          <IconButton onClick={pvlcTogglePlay} sx={{ background: "#ff6b9d", color: "#fff", width: 40, height: 40, "&:hover": { background: "#ff4d8d", transform: "scale(1.05)" }, transition: "all 0.15s" }}>
                            {pvlcPlaying ? <Pause sx={{ fontSize: "1.3rem" }} /> : <PlayArrow sx={{ fontSize: "1.3rem" }} />}
                          </IconButton>
                          <IconButton onClick={() => pvlcSkip(10)} size="small" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ff6b9d" } }}><SkipNext sx={{ fontSize: "1.4rem" }} /></IconButton>
                        </div>
                      </div>
                    )}

                    {pvlcPhase !== "idle" && (
                      <div>
                        <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Mix</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          <PvlcVolSlider icon={<VolumeUp sx={{ fontSize: "0.85rem", color: "#22d3ee" }} />} label="Video Audio" value={pvlcVideoVol} color="#22d3ee" onChange={setPvlcVideoVol} />
                          {/* B3 FIX: single slider drives pvlcMicVol which is the sole mic gain authority */}
                          <PvlcVolSlider icon={<Mic sx={{ fontSize: "0.85rem", color: "#a855f7" }} />} label="Your Mic" value={pvlcMicVol} color="#a855f7" onChange={setPvlcMicVol} />
                        </div>
                      </div>
                    )}

                    {pvlcPhase !== "idle" && (
                      <div onClick={() => setPvlcPipOn((p) => !p)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${pvlcPipOn ? "rgba(255,107,157,0.4)" : "rgba(255,255,255,0.1)"}`, borderRadius: 8, cursor: "pointer" }}>
                        <span style={{ color: "#fff", fontSize: "0.8rem", fontWeight: 600 }}>Camera PiP</span>
                        <div style={{ width: 32, height: 17, borderRadius: 9, background: pvlcPipOn ? "#ff6b9d" : "rgba(255,255,255,0.15)", position: "relative", transition: "background 0.2s" }}>
                          <div style={{ position: "absolute", top: 2, left: pvlcPipOn ? 15 : 2, width: 13, height: 13, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                        </div>
                      </div>
                    )}

                    <div style={{ flex: 1 }} />

                    {pvlcPhase === "loaded" && (
                      <button onClick={pvlcGoLive} disabled={pvlcPublishing} style={{ width: "100%", padding: "11px", background: pvlcPublishing ? "rgba(255,107,157,0.4)" : "#ff6b9d", border: "none", borderRadius: 9, color: "#fff", fontWeight: 700, fontSize: "0.86rem", letterSpacing: "0.05em", cursor: pvlcPublishing ? "not-allowed" : "pointer", boxShadow: pvlcPublishing ? "none" : "0 4px 18px rgba(255,107,157,0.35)" }}>
                        {pvlcPublishing ? "Starting…" : "▶  Go Live with PVLC"}
                      </button>
                    )}

                    {pvlcPhase === "live" && (
                      <div style={{ padding: "10px 12px", background: "rgba(255,107,157,0.07)", border: "1px solid rgba(255,107,157,0.28)", borderRadius: 8, textAlign: "center" }}>
                        <div style={{ color: "#ff6b9d", fontWeight: 700, fontSize: "0.76rem" }}>🎬 Streaming to viewers</div>
                        <div style={{ color: "rgba(255,255,255,0.38)", fontSize: "0.68rem", marginTop: 2 }}>Main: your video · PiP: your camera</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* ── Normal camera / screen-share view ─────────────────── */
              <div className="s4y-video-inner" style={{ position: "absolute", inset: 0 }}>
                <LocalUser
                  cameraOn={cameraOn || screenOn}
                  micOn={micOn}
                  playAudio={false}
                  videoTrack={screenOn && screenTrackRef.current ? screenTrackRef.current : localCameraTrack}
                  audioTrack={localMicrophoneTrack}
                  style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#0c1128" }}
                />
              </div>
            )}

            {flyingEmojis.map(({ id, emoji, x }) => (
              <span key={id} className="s4y-flying-emoji" style={{ left: `${x}%`, bottom: "22%", position: "absolute", zIndex: 10 }}>{emoji}</span>
            ))}

            {!cameraOn && !pvlcOpen && (
              <div className="s4y-cam-placeholder">
                <span className="s4y-cam-placeholder-icon">🎤</span>
                <span className="s4y-cam-placeholder-text">Your Camera</span>
              </div>
            )}

            <div className="s4y-username-badge" style={{ position: "absolute", bottom: 56, left: 12, zIndex: 9 }}>
              <span className="s4y-badge-dot" />{displayName}
            </div>
            {(trackLoaded || karaokeDuration > 0) && songTitle && (
              <div
                style={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  zIndex: 9,
                  maxWidth: "72%",
                  background: "rgba(0,0,0,0.65)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,107,157,0.35)",
                  borderRadius: 8,
                  padding: "6px 10px",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={songTitle}
              >
                ♪ {songTitle}
              </div>
            )}
            <div className="s4y-video-badge" style={{ position: "absolute", top: 12, right: 12, zIndex: 9 }}>{viewerCount}</div>

            {/* Controls bar */}
            <div className="s4y-controls-bar">
              <button className={`s4y-ctrl-btn ${micOn ? "on" : "off"}`} onClick={toggleMic} title={micOn ? "Mute" : "Unmute"}>
                {micOn ? <Mic sx={{ fontSize: "1.1rem" }} /> : <MicOff sx={{ fontSize: "1.1rem" }} />}
              </button>
              {!pvlcOpen && (
                <button className={`s4y-ctrl-btn ${cameraOn ? "on" : "off"}`} onClick={toggleCamera} title={cameraOn ? "Turn off camera" : "Turn on camera"}>
                  {cameraOn ? <Videocam sx={{ fontSize: "1.1rem" }} /> : <VideocamOff sx={{ fontSize: "1.1rem" }} />}
                </button>
              )}
              <button
                className={`s4y-ctrl-btn ${screenOn ? "on" : ""}`}
                onClick={toggleScreenShare}
                title={screenOn ? "Stop screen share" : "Share screen"}
              >
                {screenOn
                  ? <StopScreenShare sx={{ fontSize: "1.1rem" }} />
                  : <ScreenShare     sx={{ fontSize: "1.1rem" }} />}
              </button>
              <span className="s4y-ctrl-divider" />
              <button className="s4y-ctrl-btn" onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                {isFullscreen ? <FullscreenExit sx={{ fontSize: "1.1rem" }} /> : <Fullscreen sx={{ fontSize: "1.1rem" }} />}
              </button>
              <button className="s4y-ctrl-btn" onClick={() => setAudioQualityOpen(true)} title="Audio quality" style={{ position: "relative" }}>
                <GraphicEq sx={{ fontSize: "1.1rem" }} />
                {(aecEnabled || agcEnabled || ansEnabled || audioPreset !== "high_quality_stereo") && (
                  <span style={{ position: "absolute", top: 3, right: 3, width: 6, height: 6, borderRadius: "50%", background: "#f97316", pointerEvents: "none" }} />
                )}
              </button>
              <button
                className={`s4y-ctrl-btn ${pvlcOpen || pvlcPhase === "live" ? "on" : ""}`}
                onClick={() => setPvlcOpen(true)}
                title="Play Video Live Commentary"
                style={{ border: pvlcPhase === "live" ? "1px solid rgba(255,107,157,0.6)" : undefined, color: pvlcPhase === "live" ? "#ff6b9d" : undefined, background: pvlcPhase === "live" ? "rgba(255,107,157,0.2)" : undefined }}
              >
                <MovieFilter sx={{ fontSize: "1.1rem" }} />
              </button>
              <span className="s4y-ctrl-divider" />
              <button className="s4y-ctrl-btn end" onClick={() => setShowEndDialog(true)} title="End session">
                <CallEnd sx={{ fontSize: "1.1rem" }} />
              </button>
            </div>
          </div>

          {/* ── Inline MP3 upload ─────────────────────────────────────────── */}
          {!trackLoaded && (
            <>
              <div style={{ margin: "8px 0", borderRadius: 10, border: "1.5px dashed rgba(249,115,22,0.35)", background: "rgba(249,115,22,0.06)", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: "1.3rem" }}>🎵</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "rgba(255,255,255,0.85)", fontWeight: 600, fontSize: "0.82rem", marginBottom: 2 }}>No audio track loaded</div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>Upload an MP3 to arm the play button, or open the Karaoke Studio for full controls</div>
                </div>
                <input
                  type="file"
                  id="s4y-inline-mp3"
                  accept="audio/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const filename = f.name;
                    setKaraokeFilename(filename);
                    // Broadcast immediately so Cafe can start LRCLIB lookup from filename.
                    console.debug("[Sing4You Talent] MP3 loaded", { filename });
                    console.debug("[Sing4You Talent → RTM] karaoke_track (on upload)", { filename, duration: karaokeDurationRef.current, time: 0 });
                    sendRTM(rtmChannelRef, { type: "karaoke_track", filename, duration: karaokeDurationRef.current, time: 0 });
                    audioMixRef.current?.loadFile(f, filename);
                    enqueueSnackbar(`Loading "${filename}"…`, { variant: "info", autoHideDuration: 3000 });
                    resetLyrics();
                    fetchForFilename(filename);
                    e.target.value = "";
                  }}
                />
                <label htmlFor="s4y-inline-mp3" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f97316", color: "#fff", borderRadius: 8, padding: "7px 14px", fontWeight: 700, fontSize: "0.78rem", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>↑ Upload MP3</label>
                <button style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, padding: "7px 12px", fontWeight: 600, fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => setAudioMixOpen(true)}>Studio ⚙</button>
              </div>
              {/* Lyrics auto-fetch status — shown below the upload strip */}
              {lyricsResult.status !== "idle" && (
                <div style={{ marginTop: 8 }}>
                  <LyricsStatusBadge
                    status={lyricsResult.status}
                    title={lyricsResult.title}
                    artist={lyricsResult.artist}
                    onConfirm={() => {
                      // Talent accepted the low-confidence match — promote to found/plain
                      confirmResult();
                    }}
                    onReject={() => {
                      // Talent rejected the match — reset so they can upload manually
                      resetLyrics();
                    }}
                    onManualUpload={() => setAudioMixOpen(true)}
                  />
                </div>
              )}
            </>
          )}

          <KaraokeLyrics
            lrc={lyricsLrc} currentTime={lyricsTime} duration={karaokeDuration}
            songTitle={songTitle || undefined} isPlaying={isKaraokePlaying} volume={volume}
            onPlayPause={handlePlayPause} onPrev={handlePrev} onNext={handleNext}
            onSeek={handleSeek} onVolumeChange={handleVolumeChange}
            onSettingsClick={() => setAudioMixOpen(true)}
          />

          {/* Creator tools moved into the right sidebar tabs (Bingo / Songs). */}
        </main>

        {/* ── Re-open sidebar pill (when sidebar is closed) ─ */}
        {!sidebarOpen && (
          <Tooltip title="Open Chat & panel">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              style={{
                position: "absolute",
                top: "50%",
                right: 0,
                transform: "translateY(-50%)",
                writingMode: "vertical-rl",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.85)",
                background: "rgba(15,23,42,0.95)",
                border: "1px solid rgba(148,163,184,0.4)",
                borderRight: "none",
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                padding: "12px 8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "-4px 0 12px rgba(0,0,0,0.3)",
                zIndex: 10,
              }}
            >
              <ChatBubbleOutline sx={{ fontSize: "1rem" }} />
              Chat
            </button>
          </Tooltip>
        )}

        {/* ── Sidebar (Chat / Guests / Queue / Polls / Bingo / Songs) — can be closed by talent ─ */}
        <aside
          className="s4y-sidebar"
          style={{
            width: isMobile ? "100%" : sidebarOpen ? undefined : 0,
            minWidth: isMobile ? "100%" : sidebarOpen ? undefined : 0,
            overflow: "hidden",
            borderLeft: isMobile ? "none" : sidebarOpen ? undefined : "none",
            borderTop: isMobile ? "1px solid rgba(148,163,184,0.3)" : undefined,
            transition: "width 0.2s ease, min-width 0.2s ease",
          }}
        >
          <div className="s4y-sidebar-tabs" style={{ display: sidebarOpen ? "flex" : "none", alignItems: "center" }}>
            <Tooltip title="Scroll tabs left">
              <IconButton
                size="small"
                onClick={() => scrollTabs("left")}
                className="s4y-sidebar-tab-arrow"
                sx={{ color: "rgba(255,255,255,0.55)", "&:hover": { color: "#fff" } }}
              >
                ‹
              </IconButton>
            </Tooltip>

            <div ref={tabsScrollRef} className="s4y-sidebar-tabs-scroll">
              {(
                [
                  { id: "chat",     label: "CHAT"   },
                  { id: "guests",   label: "GUESTS" },
                  { id: "youtube",  label: "QUEUE"  },
                  { id: "polls",    label: "POLLS"  },
                  { id: "bingo",    label: "BINGO"  },
                ] as { id: SidebarTab; label: string }[]
              ).map(({ id, label }) => (
                <button
                  key={id}
                  className={`s4y-sidebar-tab ${activeTab === id ? "active" : ""}`}
                  onClick={() => setActiveTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <Tooltip title="Scroll tabs right">
              <IconButton
                size="small"
                onClick={() => scrollTabs("right")}
                className="s4y-sidebar-tab-arrow"
                sx={{ color: "rgba(255,255,255,0.55)", "&:hover": { color: "#fff" } }}
              >
                ›
              </IconButton>
            </Tooltip>
          </div>
          <div
            className="s4y-chat-wrap"
            style={{ display: sidebarOpen || isMobile ? "block" : "none" }}
          >
            {activeTab === "chat"    && (bookingId ? <LiveChat bookingId={bookingId} username={displayName} height="100%" /> : <div className="s4y-tool-empty"><div className="s4y-tool-empty-icon">💬</div><div className="s4y-tool-empty-title">Live Chat</div></div>)}
            {activeTab === "guests"  && <div className="s4y-tool-pane"><div className="s4y-tool-tab-header"><span className="s4y-tool-tab-header-icon">👥</span><span className="s4y-tool-tab-header-title">Guest Management</span><span className="s4y-tool-tab-header-pill">LIVE</span></div><div className="s4y-tool-content">{bookingId ? <GuestManagement bookingId={bookingId} /> : <div className="s4y-tool-empty"><div className="s4y-tool-empty-icon">👥</div></div>}</div></div>}
            {activeTab === "youtube" && <div className="s4y-tool-pane"><div className="s4y-tool-tab-header"><span className="s4y-tool-tab-header-icon">▶️</span><span className="s4y-tool-tab-header-title">YouTube Requests</span><span className="s4y-tool-tab-header-pill">QUEUE</span></div><div className="s4y-tool-content">{bookingId ? <YoutubeRequest bookingId={bookingId} /> : <div className="s4y-tool-empty"><div className="s4y-tool-empty-icon">▶️</div></div>}</div></div>}
            {activeTab === "polls"   && <div className="s4y-tool-pane"><div className="s4y-tool-tab-header"><span className="s4y-tool-tab-header-icon">📊</span><span className="s4y-tool-tab-header-title">Live Polls</span><span className="s4y-tool-tab-header-pill">ENGAGE</span></div><div className="s4y-tool-content">{bookingId ? <LivePolls bookingId={bookingId} /> : <div className="s4y-tool-empty"><div className="s4y-tool-empty-icon">📊</div></div>}</div></div>}
            {activeTab === "bingo"   && <div className="s4y-tool-pane"><div className="s4y-tool-tab-header"><span className="s4y-tool-tab-header-icon">🎯</span><span className="s4y-tool-tab-header-title">Bingo Break</span><span className="s4y-tool-tab-header-pill">PLAY</span></div><div className="s4y-tool-content">{bookingId ? <BingoManagement bookingId={bookingId} /> : <div className="s4y-tool-empty"><div className="s4y-tool-empty-icon">🎯</div></div>}</div></div>}
          </div>
          <div className="s4y-reactions">
            <div className="s4y-section-label">React</div>
            <div className="s4y-reactions-grid">
              {["🔥","❤️","🎤","⭐","🎵","🎶","🎼","💕"].map((emoji) => <button key={emoji} className="s4y-react-btn">{emoji}</button>)}
            </div>
          </div>
          <div className="s4y-chat-input-row">
            <input className="s4y-chat-input" placeholder="Say something..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setChatInput("")} />
            <button className="s4y-chat-send" onClick={() => setChatInput("")}><Send sx={{ fontSize: "0.95rem" }} /></button>
          </div>
        </aside>
      </div>

      {/* Creator Tools drawer removed — Bingo/Songlist are now sidebar tabs. */}

      {/* ── AudioMixUpdated ───────────────────────────────────────────────── */}
      <div className="s4y-audiomix-overlay" style={{ display: audioMixOpen ? undefined : "none" }} onClick={() => setAudioMixOpen(false)}>
        <div className="s4y-audiomix-modal" onClick={(e) => e.stopPropagation()}>
          <AudioMixUpdated ref={audioMixRef} open={audioMixOpen} keepPlayingOnClose sessionId={bookingId}
            onClose={() => setAudioMixOpen(false)} onLyricsChange={handleLyricsChange}
            onLyricsTimeChange={(t) => setLyricsTime(t)} onPlayStateChange={(p) => setIsKaraokePlaying(p)}
            onDurationChange={(d) => { setKaraokeDuration(d); setTrackLoaded(d > 0); if (d === 0) { resetLyrics(); } }}
          />
        </div>
      </div>

      {/* ── Audio Quality dialog ──────────────────────────────────────────── */}
      <Dialog open={audioQualityOpen} onClose={() => setAudioQualityOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { background: "#0f1225", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 } }}>
        <DialogTitle sx={{ color: "#fff", pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
          <GraphicEq sx={{ color: "#f97316", fontSize: "1.2rem" }} /> Audio Quality &amp; Devices
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>Input Device — Microphone</Typography>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {micDevices.length === 0 ? <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>No microphones detected</Typography>
              : micDevices.map((dev) => { const active = activeMicId === dev.deviceId; return (
                <button key={dev.deviceId} onClick={() => handleMicDeviceChange(dev.deviceId)} style={{ display: "flex", alignItems: "center", gap: 10, background: active ? "rgba(249,115,22,0.13)" : "rgba(255,255,255,0.04)", border: `1px solid ${active ? "#f97316" : "rgba(255,255,255,0.1)"}`, borderRadius: 8, color: active ? "#f97316" : "rgba(255,255,255,0.75)", padding: "9px 14px", cursor: "pointer", textAlign: "left", width: "100%", fontSize: "0.8rem" }}>
                  <span style={{ fontSize: "1rem" }}>🎙️</span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{dev.label || `Microphone ${micDevices.indexOf(dev) + 1}`}</span>
                  {active && <span style={{ fontSize: "0.65rem", fontWeight: 700, background: "rgba(249,115,22,0.25)", color: "#f97316", border: "1px solid rgba(249,115,22,0.4)", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>ACTIVE</span>}
                </button>); })}
          </div>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>Output Device — Speakers / Headphones</Typography>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
            {outputDevices.length === 0 ? <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>No output devices detected</Typography>
              : outputDevices.map((dev) => { const active = activeOutputId === dev.deviceId; return (
                <button key={dev.deviceId} onClick={() => handleOutputDeviceChange(dev.deviceId)} style={{ display: "flex", alignItems: "center", gap: 10, background: active ? "rgba(99,102,241,0.13)" : "rgba(255,255,255,0.04)", border: `1px solid ${active ? "#818cf8" : "rgba(255,255,255,0.1)"}`, borderRadius: 8, color: active ? "#818cf8" : "rgba(255,255,255,0.75)", padding: "9px 14px", cursor: "pointer", textAlign: "left", width: "100%", fontSize: "0.8rem" }}>
                  <span style={{ fontSize: "1rem" }}>🔊</span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{dev.label || `Speaker ${outputDevices.indexOf(dev) + 1}`}</span>
                  {active && <span style={{ fontSize: "0.65rem", fontWeight: 700, background: "rgba(99,102,241,0.25)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.4)", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>ACTIVE</span>}
                </button>); })}
          </div>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.25)", display: "block", mb: 2.5, lineHeight: 1.6 }}>ℹ️ Switching mic takes effect immediately. Output routing requires Chrome 110+.</Typography>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginBottom: 16 }} />
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>Encoding Preset</Typography>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {([{ id: "high_quality_stereo", label: "High Quality Stereo", detail: "48 kHz · Stereo · 128 kbps — recommended for karaoke" }, { id: "high_quality", label: "High Quality", detail: "48 kHz · Mono · 128 kbps — voice-only" }, { id: "standard", label: "Standard", detail: "32 kHz · Mono · 64 kbps — low-bandwidth fallback" }] as any[]).map(({ id, label, detail }) => { const active = audioPreset === id; return (
              <button key={id} onClick={() => { setAudioPreset(id); applyEncoderPreset(id); }} style={{ background: active ? "rgba(249,115,22,0.15)" : "rgba(255,255,255,0.04)", border: `1px solid ${active ? "#f97316" : "rgba(255,255,255,0.1)"}`, borderRadius: 8, color: active ? "#f97316" : "rgba(255,255,255,0.75)", padding: "10px 14px", cursor: "pointer", textAlign: "left", width: "100%" }}>
                <div style={{ fontWeight: 600, fontSize: "0.82rem", marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: "0.71rem", opacity: 0.6 }}>{detail}</div>
              </button>); })}
          </div>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mt: 2.5, mb: 0.5 }}>3A Processing</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", display: "block", lineHeight: 1.6, mb: 1.5 }}>OFF by default. Turn on AEC only if monitoring through speakers.</Typography>
          {([{ key: "aec", label: "Echo Cancellation", abbr: "AEC", desc: "Prevents mic picking up speaker output", value: aecEnabled, onToggle: handleToggleAEC }, { key: "agc", label: "Auto Gain Control", abbr: "AGC", desc: "Automatically adjusts mic volume", value: agcEnabled, onToggle: handleToggleAGC }, { key: "ans", label: "Noise Suppression", abbr: "ANS", desc: "Filters background noise — can mute instruments", value: ansEnabled, onToggle: handleToggleANS }]).map(({ key, label, abbr, desc, value, onToggle }) => (
            <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ flex: 1, paddingRight: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 600, fontSize: "0.82rem" }}>{label}</Typography>
                  <span style={{ fontSize: "0.62rem", fontWeight: 700, background: value ? "rgba(249,115,22,0.2)" : "rgba(255,255,255,0.08)", color: value ? "#f97316" : "rgba(255,255,255,0.4)", border: `1px solid ${value ? "rgba(249,115,22,0.4)" : "rgba(255,255,255,0.12)"}`, borderRadius: 4, padding: "1px 5px" }}>{abbr}</span>
                </div>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.38)", lineHeight: 1.5 }}>{desc}</Typography>
              </div>
              <Switch checked={value} onChange={onToggle} size="small" sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#f97316" }, "& .MuiSwitch-track": { backgroundColor: "rgba(255,255,255,0.2)" } }} />
            </div>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAudioQualityOpen(false)} variant="outlined" size="small" sx={{ borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ── End / Leave dialog ───────────────────────────────────────────── */}
      <Dialog open={showEndDialog} onClose={() => setShowEndDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { background: "#0f1225", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 } }}>
        <DialogTitle sx={{ color: "#fff", pb: 1 }}>End Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 1.5 }}>What would you like to do?</Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}><strong style={{ color: "rgba(255,255,255,0.8)" }}>Leave</strong> — you exit but the session continues for others.</Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 0.75 }}><strong style={{ color: "rgba(255,255,255,0.8)" }}>End Session</strong> — terminates the stream for everyone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setShowEndDialog(false)} variant="outlined" size="small" sx={{ borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" }}>Cancel</Button>
          <Button onClick={handleLeave} variant="outlined" color="warning" size="small">Leave</Button>
          <Button onClick={handleEndStream} variant="contained" color="error" size="small" disabled={startEndStageSession.isLoading}>End Session</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}

export function Sing4YouTalentPage() {
  return (
    <AgoraWrapper mode="live" role="host">
      <Sing4YouLiveInner />
    </AgoraWrapper>
  );
}
export default Sing4YouTalentPage;